import { Alert, Button } from "react-bootstrap";
import {
  Controller,
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import { useQuery } from "@tanstack/react-query";
import { Dropdown } from "primereact/dropdown";

import LocationTeamForm from "./LocationTeamForm";
import edit from "../../../../assets/svg/edit.svg";
import Input from "../../../../common/components/form/Input";
import Loader from "../../../../common/components/Loader";
import { Permissions } from "../../../../common/enums/sharedEnums.ts";
import history from "../../../../routes/history";
import {
  useCreateTeam,
  useGetAllTeamServiceTypes,
  useUpdateTeam,
} from "../../hooks/team.hooks.ts";
import { IManagementRole } from "../../interfaces/management";
import { ITeam, TeamServiceTypeOptions } from "../../interfaces/team";
import { MANAGEMENT, TEAM } from "../../routes/ProfileRoutes";
import { getRoles } from "../../services";

interface ITeamFormProps {
  defaultValues?: ITeam;
  agencyId?: string;
  readOnly?: boolean;
}

const TeamForm: React.FC<ITeamFormProps> = ({
  defaultValues,
  agencyId,
  readOnly,
}) => {
  const {
    data: roles,
    isLoading,
    error,
  } = useQuery<IManagementRole[], Error>(["team_role_management"], () =>
    getRoles()
  );

  const { mutate: createTeam, isLoading: isCreating } = useCreateTeam({
    handleSuccess: (data) => {
      toast.success(`L'équipe ${(data as ITeam)?.label} a été créée !`);
    },
  });

  const { mutate: updateTeam, isLoading: isUpdating } = useUpdateTeam({
    handleSuccess: (data) => {
      toast.success(`L'équipe ${(data as ITeam)?.label} a été mis à jour !`);
    },
  });

  const { data: serviceTypes } = useGetAllTeamServiceTypes();

  const serviceTypeOptions = Array.isArray(serviceTypes)
    ? serviceTypes?.map((item: TeamServiceTypeOptions) => ({
        label: item?.label,
        value: item?.value,
      }))
    : [];

  const methods = useForm<FieldValues>({
    defaultValues: {
      ...defaultValues,
      ...{
        sectors: defaultValues?.sectors ?? [],
        radii: defaultValues?.radii ?? [],
      },
    },
  });

  const onSubmit = async (data: ITeam) => {
    try {
      delete data.members;
      if (data?.id) {
        // update
        updateTeam(data);
      } else {
        // create
        if (!agencyId) {
          throw "Id de l'agence non trouvé.";
        }
        data.agencyId = agencyId;
        createTeam(data);
      }
      history.push(MANAGEMENT);
    } catch (e: any) {
      toast.error("Impossible de valider le formulaire");
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <Alert variant="danger">
        Impossible de récupérer la liste des rôles d'équipe.
      </Alert>
    );
  }

  if (!roles) {
    return (
      <Alert variant="danger">
        Erreur de récupération des données : Veuillez réessayer ultérieurement.
      </Alert>
    );
  }

  return (
    <div className="bg-container teamForm">
      <div className="d-flex justify-content-between">
        <h2 className="mt-1 fw-bold" style={{ fontSize: "24px" }}>
          {defaultValues ? `Équipe ${defaultValues.label}` : "Nouvelle Équipe"}
        </h2>
        {readOnly &&
          defaultValues?.permissions?.includes(Permissions.TEAM_UPDATE) && (
            <Link
              to={{
                pathname: TEAM.replace(":id?", defaultValues?.id || ""),
                state: { readOnly: false, parentId: agencyId },
              }}
              replace
              className="btn btn-actions btn-edit-order"
              style={{ height: "36px", width: "36px" }}
            >
              <img
                src={edit}
                alt="edit"
                style={{ width: "12px", height: "12px" }}
              />
            </Link>
          )}
      </div>
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(
            onSubmit as SubmitHandler<FieldValues>
          )}
          className="teamForm-form"
        >
          <div className="teamForm-form-details">
            <Input
              control={methods.control}
              label="Nom"
              name={"label"}
              defaultValue=""
              rules={{ required: "Veuillez renseigner un nom" }}
              disabled={readOnly}
            />
            <Controller
              control={methods.control}
              name={"roleId"}
              render={({ field, fieldState }) => (
                <div className="d-flex flex-column gap-1">
                  <Dropdown
                    disabled={readOnly}
                    value={field.value}
                    onChange={(e) => field.onChange(e.value)}
                    options={roles.map((item) => {
                      return { label: item.label, value: item.id };
                    })}
                    placeholder="Sélectionnez un rôle"
                    className="w-full md:w-14rem"
                  />
                  {fieldState?.error && (
                    <span className="form-input_error-message">
                      {fieldState.error.message}
                    </span>
                  )}
                </div>
              )}
              rules={{ required: "Veuillez renseigner un rôle" }}
            />
          </div>
          <LocationTeamForm disabled={readOnly} />
          <div>
            <label className="form-input_label fw-bold small mb-1">
              Type de service :
            </label>
            <Controller
              control={methods.control}
              name={"serviceType"}
              render={({ field, fieldState }) => (
                <div className="d-flex flex-column gap-1">
                  <Dropdown
                    disabled={readOnly}
                    value={field.value}
                    onChange={(e) => field.onChange(e.value)}
                    options={serviceTypeOptions}
                    placeholder="Sélectionner un type de service"
                    className="w-full dropdown-left-aligned"
                    itemTemplate={(option) => (
                      <div className="text-start">{option?.label}</div>
                    )}
                  />
                  {fieldState?.error && (
                    <span className="form-input_error-message">
                      {fieldState.error.message}
                    </span>
                  )}
                </div>
              )}
              rules={{ required: "Veuillez renseigner un type de service" }}
            />
          </div>
          {!readOnly && (
            <div className="d-flex justify-content-start">
              <Button
                variant={"primary"}
                disabled={isCreating || isUpdating}
                type={"submit"}
              >
                {defaultValues ? "Modifier" : "Ajouter"}
              </Button>
            </div>
          )}
        </form>
      </FormProvider>
    </div>
  );
};

export default TeamForm;
